export type HomeArticle = {
  type: "article";
  global_id: string;
  article_id: string;
  issue_id: string;
  section: string;
  title: string;
  summary: string;
  byline: string | null;
  tags: string[];
  page_start: number;
  page_end: number;
  publication: string;
  issue_title: string;
  issue_label: string;
  issue_date: string | null;
  reading_minutes: number;
  cover_url: string | null;
};

export type HomePodcast = {
  type: "podcast";
  id: string;
  title: string;
  feed_title: string;
  category: string;
  summary: string;
  published_at: string | null;
  duration_seconds: number | null;
  artwork_url: string | null;
  progress_position: number | null;
  progress_ratio: number | null;
};

export type ContinueReadingItem = {
  type: "issue";
  id: string;
  title: string;
  publication: string;
  issue_label: string;
  issue_date: string | null;
  page_count: number;
  page_number: number;
  progress: number;
  cover_url: string | null;
  updated_at: string;
};

export type HomeIssue = {
  id: string;
  title: string;
  publication: string;
  issue_label: string;
  cover_url: string | null;
};

export type PopularArticle = HomeArticle & {
  unique_readers: number;
  engaged_reads: number;
  completions: number;
};

export type HomePayload = {
  guide: HomeArticle[];
  latest_articles: HomeArticle[];
  latest_podcasts: HomePodcast[];
  continue_reading: ContinueReadingItem[];
  popular: PopularArticle[];
};

type HomeSectionsProps = {
  home: HomePayload;
  latestIssues: HomeIssue[];
  onArticle: (article: HomeArticle, source: "home" | "library") => void;
  onContinue: (item: ContinueReadingItem) => void;
  onIssue: (issue: HomeIssue) => void;
  onPodcast: (podcast: HomePodcast) => void;
  onViewPeriodicals: () => void;
  onViewPodcasts: () => void;
};

function formatIssueDate(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "numeric",
    day: "numeric",
  }).format(date);
}

function articleMeta(article: HomeArticle): string {
  return [
    article.publication,
    article.issue_label,
    `${article.reading_minutes} 分钟`,
  ].join(" · ");
}

function podcastMeta(podcast: HomePodcast): string {
  const duration = podcast.duration_seconds
    ? `${Math.round(podcast.duration_seconds / 60)} 分钟`
    : "长播客";
  return [podcast.feed_title, podcast.category, duration].join(" · ");
}

export function HomeSections({
  home,
  latestIssues,
  onArticle,
  onContinue,
  onIssue,
  onPodcast,
  onViewPeriodicals,
  onViewPodcasts,
}: HomeSectionsProps): React.JSX.Element {
  const [lead, ...secondary] = home.guide;
  const secondaryItems: Array<HomeArticle | HomePodcast> = [];
  const secondaryCount = Math.max(secondary.length, home.latest_podcasts.length);
  for (let index = 0; index < secondaryCount; index += 1) {
    const article = secondary[index];
    const podcast = home.latest_podcasts[index];
    if (article) secondaryItems.push(article);
    if (podcast) secondaryItems.push(podcast);
    if (secondaryItems.length >= 4) break;
  }
  const podcastContinuations = home.latest_podcasts.filter(
    (podcast) =>
      podcast.progress_ratio !== null
      && podcast.progress_ratio > 0
      && podcast.progress_ratio < 0.98,
  );
  const continuationItems: Array<ContinueReadingItem | HomePodcast> = [
    ...home.continue_reading.filter((item) => item.progress > 0 && item.progress < 0.98),
    ...podcastContinuations,
  ].slice(0, 4);

  return (
    <>
      {continuationItems.length > 0 && (
        <section className="continue-reading" aria-labelledby="continue-title">
          <header>
            <p className="eyebrow">FOR YOU</p>
            <h2 id="continue-title">接着读</h2>
          </header>
          <div>
            {continuationItems.map((item) => {
              const progress = item.type === "issue"
                ? item.progress
                : item.progress_ratio ?? 0;
              return (
                <button
                  key={`${item.type}-${item.id}`}
                  onClick={() => {
                    if (item.type === "issue") onContinue(item);
                    else onPodcast(item);
                  }}
                >
                  <span>
                    {item.type === "issue"
                      ? `期刊 · ${item.publication} · ${item.issue_label}`
                      : `播客 · ${item.feed_title}`}
                  </span>
                  <strong>{item.title}</strong>
                  <span className="continue-progress">
                    <i style={{ width: `${Math.round(progress * 100)}%` }} />
                  </span>
                  <small>
                    {item.type === "issue"
                      ? `上次读到第 ${item.page_number} 页`
                      : `上次听到 ${Math.floor((item.progress_position ?? 0) / 60)} 分钟`}
                    {` · ${Math.round(progress * 100)}%`}
                  </small>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {lead && (
        <section className="weekly-guide" aria-labelledby="weekly-guide-title">
          <header className="section-heading weekly-guide-heading">
            <div>
              <p className="eyebrow">WEEKLY SELECTION</p>
              <h2 id="weekly-guide-title">本周精选</h2>
            </div>
          </header>

          <div className="weekly-guide-grid">
            <button
              className="guide-lead"
              onClick={() => onArticle(lead, "home")}
            >
              <span className="guide-lead-copy">
                <span className="guide-index">01</span>
                <span className="guide-section">期刊 · {lead.section}</span>
                <strong>{lead.title}</strong>
                <span className="guide-summary">{lead.summary}</span>
                <span className="guide-meta">{articleMeta(lead)} <i>阅读全文 →</i></span>
              </span>
              {lead.cover_url && (
                <img src={lead.cover_url} alt="" loading="eager" />
              )}
            </button>

            <ol className="guide-secondary">
              {secondaryItems.map((item, index) => (
                <li key={item.type === "article" ? item.global_id : item.id}>
                  <button
                    onClick={() => {
                      if (item.type === "article") onArticle(item, "home");
                      else onPodcast(item);
                    }}
                  >
                    <span className="guide-index">{String(index + 2).padStart(2, "0")}</span>
                    <span>
                      <small>
                        {item.type === "article"
                          ? `${item.publication} · ${item.section} · ${item.reading_minutes} 分钟`
                          : `播客 · ${podcastMeta(item)}`}
                      </small>
                      <strong>{item.title}</strong>
                      <em>{item.summary}</em>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {latestIssues.length > 0 && (
        <section className="home-channel-section" aria-labelledby="latest-periodicals-title">
          <header className="section-heading">
            <div>
              <p className="eyebrow">PERIODICALS</p>
              <h2 id="latest-periodicals-title">最新期刊</h2>
            </div>
            <button className="section-link" onClick={onViewPeriodicals}>查看全部期刊 →</button>
          </header>
          <div className="home-issue-grid">
            {latestIssues.slice(0, 4).map((issue) => (
              <button className="home-issue-card" key={issue.id} onClick={() => onIssue(issue)}>
                <span className="home-issue-cover">
                  {issue.cover_url ? (
                    <img src={issue.cover_url} alt={`${issue.title}封面`} loading="lazy" />
                  ) : (
                    <span><b>{issue.publication}</b><small>{issue.issue_label}</small></span>
                  )}
                </span>
                <span className="home-issue-copy">
                  <small>{issue.publication}</small>
                  <strong>{issue.title}</strong>
                  <em>{issue.issue_label}</em>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {home.latest_podcasts.length > 0 && (
        <section className="home-channel-section" aria-labelledby="latest-podcasts-title">
          <header className="section-heading">
            <div>
              <p className="eyebrow">PODCASTS</p>
              <h2 id="latest-podcasts-title">最新播客</h2>
            </div>
            <button className="section-link" onClick={onViewPodcasts}>查看全部播客 →</button>
          </header>
          <ol className="home-podcast-list">
            {home.latest_podcasts.slice(0, 5).map((podcast, index) => (
              <li key={podcast.id}>
                <button onClick={() => onPodcast(podcast)}>
                  <span className="arrival-index">{String(index + 1).padStart(2, "0")}</span>
                  <span className="arrival-copy">
                    <small>{podcast.feed_title} · {formatIssueDate(podcast.published_at)}</small>
                    <strong>{podcast.title}</strong>
                    <em>{podcast.summary}</em>
                  </span>
                  <span className="arrival-meta">
                    {podcast.duration_seconds
                      ? `${Math.round(podcast.duration_seconds / 60)} 分钟`
                      : "长播客"}
                    <i>{podcast.category}</i>
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </section>
      )}

      {home.popular.length > 0 && (
        <section className="popular-reading" aria-labelledby="popular-title">
          <header className="section-heading">
            <div>
              <p className="eyebrow">IN THE READING ROOM</p>
              <h2 id="popular-title">近期在读</h2>
            </div>
            <span>仅统计有效阅读，不含重复刷新</span>
          </header>
          <ol>
            {home.popular.map((article) => (
              <li key={article.global_id}>
                <button onClick={() => onArticle(article, "home")}>
                  <strong>{article.title}</strong>
                  <span>{article.unique_readers} 位同事近期读过</span>
                </button>
              </li>
            ))}
          </ol>
        </section>
      )}
    </>
  );
}
